import {
  Download,
  ExternalLink,
  MapPin,
  Search,
  Send,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AttendanceLocation, thaiTimeShort, workDuration } from "./types";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

interface LiveBoardTabProps {
  sheetMonth: string;
  setSheetMonth: (month: string) => void;
  sheetDashboard: any;
  dashboard: any;
  downloadExcel: () => void;
  exportAttendance: any;
  selectedLocation: AttendanceLocation | null;
  setSelectedLocation: (loc: AttendanceLocation | null) => void;
  query: string;
  setQuery: (q: string) => void;
  filtered: string[];
  idToken?: string | null;
  systemConfigs?: Record<string, string>;
}

export function LiveBoardTab({
  sheetMonth,
  setSheetMonth,
  sheetDashboard,
  dashboard,
  downloadExcel,
  exportAttendance,
  selectedLocation,
  setSelectedLocation,
  query,
  setQuery,
  filtered,
  idToken,
  systemConfigs,
}: LiveBoardTabProps) {
  const sendSummaryMutation = trpc.attendance.sendDailyGroupSummary.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        toast.success("📢 ส่งสรุปยอดเข้ากลุ่ม LINE เรียบร้อยแล้ว!");
      } else {
        toast.warning(data.error || "ระบบส่งคำขอแล้วแต่ LINE API รายงานข้อผิดพลาด");
      }
    },
    onError: (err) => {
      toast.error(err.message || "เกิดข้อผิดพลาดในการส่งเข้ากลุ่ม LINE");
    },
  });
  const decideLateReason = trpc.attendance.decideLateReason.useMutation({
    onSuccess: async () => {
      toast.success("บันทึกผลพิจารณาเหตุผลมาสายแล้ว");
      await dashboard?.refetch?.();
    },
    onError: (error) => toast.error(error.message),
  });

  const handleSendToGroup = () => {
    if (!idToken) {
      toast.error("กรุณาเข้าสู่ระบบก่อนดำเนินการ");
      return;
    }
    const groupId = systemConfigs?.lineSummaryGroupId?.trim();
    if (!groupId) {
      toast.error("ยังไม่ได้ระบุ LINE Group ID กรุณาไปที่แท็บ 'ตั้งค่าระบบ' เพื่อใส่รหัสกลุ่มก่อน");
      return;
    }
    sendSummaryMutation.mutate({
      idToken,
      groupId,
      workDate: dashboard.data?.workDate,
    });
  };

  return (
    <div className="space-y-5">
      <Card className="glass-panel rounded-[2rem] border-white/90 shadow-xl overflow-hidden">
        <CardHeader className="p-5 sm:p-7 border-b border-slate-100">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-teal-600">
                GOOGLE SHEET MONTHLY DASHBOARD
              </span>
              <CardTitle className="text-xl font-black text-slate-900">
                ภาพรวมข้อมูลจาก Google Sheet
              </CardTitle>
              <CardDescription className="text-xs font-semibold text-slate-500 mt-0.5">
                อ้างอิงแท็บรายสัปดาห์ตั้งแต่เดือนสิงหาคม 2569
              </CardDescription>
            </div>
            <input
              type="month"
              min="2026-08"
              value={sheetMonth}
              onChange={(event) => setSheetMonth(event.target.value)}
              className="glass-input h-10 rounded-xl px-3 text-sm font-bold w-full sm:w-auto min-w-[160px]"
            />
          </div>
        </CardHeader>
        <CardContent className="p-5 sm:p-7">
          {sheetDashboard.isLoading ? (
            <p className="text-sm font-semibold text-slate-500">
              กำลังดึงข้อมูลจาก Google Sheet...
            </p>
          ) : sheetDashboard.error ? (
            <p className="text-sm font-semibold text-rose-600">
              {sheetDashboard.error.message}
            </p>
          ) : sheetDashboard.data ? (
            <>
              <div className="executive-summary-grid grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  ["พนักงาน", sheetDashboard.data.totals.employees, "text-slate-950", "bg-slate-100 border-slate-400"],
                  ["เข้าไซต์งาน", sheetDashboard.data.totals.office, "text-emerald-900", "bg-emerald-100 border-emerald-500"],
                  ["วันหยุด", sheetDashboard.data.totals.holiday, "text-blue-950", "bg-blue-100 border-blue-500"],
                  ["ไม่ได้ลงเวลา", sheetDashboard.data.totals.missing, "text-rose-950", "bg-rose-100 border-rose-500"],
                ].map(([label, value, color, surface]) => (
                  <div
                    key={String(label)}
                    className={`executive-summary-card min-h-[112px] rounded-2xl border-2 p-3 text-center flex flex-col items-center justify-center ${surface}`}
                  >
                    <p className="text-base sm:text-lg font-black leading-tight text-slate-800">{label}</p>
                    <p className={`mt-1 font-numeric text-4xl sm:text-5xl leading-none font-black tracking-tight ${color}`}>{value}</p>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] font-semibold text-slate-400">
                ข้อมูล {sheetDashboard.data.totals.records} รายการ ·{" "}
                {sheetDashboard.data.sourceTabs.length} แท็บรายสัปดาห์
              </p>
            </>
          ) : null}
        </CardContent>
      </Card>

      {dashboard.data && (
        <Card className="glass-panel rounded-[2rem] border-white/90 shadow-xl overflow-hidden">
          <CardHeader className="p-5 sm:p-7 border-b border-slate-100">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-rose-500 font-numeric">
                  DAILY LIVE BOARD
                </span>
                <CardTitle className="text-xl font-black text-slate-900">
                  ภาพรวมเวลาเข้า-ออกประจำวัน
                </CardTitle>
                <CardDescription className="text-xs font-semibold text-slate-500 mt-0.5">
                  ประจำวันที่ {dashboard.data.workDate}
                </CardDescription>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSendToGroup}
                  disabled={sendSummaryMutation.isPending}
                  className="rounded-xl font-bold text-xs bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                >
                  <Send size={14} className="mr-1.5" />
                  {sendSummaryMutation.isPending ? "กำลังส่งเข้ากลุ่ม..." : "📢 ส่งสรุปยอดเข้ากลุ่ม LINE"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadExcel}
                  disabled={exportAttendance.isFetching}
                  className="rounded-xl font-bold text-xs"
                >
                  <Download size={14} className="mr-1.5" />
                  ส่งออก Excel
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-5 sm:p-7 space-y-6">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl bg-slate-100/80 p-4 text-center border border-slate-200/80">
                <p className="text-xs font-bold text-slate-500">เข้างานแล้ว</p>
                <p className="text-3xl font-black font-numeric text-slate-900 mt-1">
                  {dashboard.data.totals.total}
                </p>
                <p className="text-[11px] font-semibold text-slate-400">คน</p>
              </div>

              <div className="rounded-2xl bg-emerald-50/80 p-4 text-center border border-emerald-100">
                <p className="text-xs font-bold text-emerald-800">ออกงานแล้ว</p>
                <p className="text-3xl font-black font-numeric text-emerald-700 mt-1">
                  {dashboard.data.totals.checkedOut}
                </p>
                <p className="text-[11px] font-semibold text-emerald-600">คน</p>
              </div>

              <div className="rounded-2xl bg-amber-50/80 p-4 text-center border border-amber-100">
                <p className="text-xs font-bold text-amber-800">กำลังทำงาน</p>
                <p className="text-3xl font-black font-numeric text-amber-700 mt-1">
                  {dashboard.data.totals.pendingCheckout}
                </p>
                <p className="text-[11px] font-semibold text-amber-600">คน</p>
              </div>
            </div>

            {selectedLocation && (
              <div className="overflow-hidden rounded-2xl border-2 border-slate-800 bg-white shadow-lg">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-slate-800 bg-slate-950 px-4 py-3 text-white">
                  <div className="flex items-center gap-2">
                    <MapPin size={18} className="text-teal-300" />
                    <div>
                      <p className="text-sm font-black">
                        พิกัด{selectedLocation.kind} · {selectedLocation.displayName}
                      </p>
                      <p className="text-[11px] font-semibold text-slate-300">
                        {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)} · คลาดเคลื่อนประมาณ{" "}
                        {Math.round(selectedLocation.accuracy ?? 0)} ม.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={`https://www.google.com/maps?q=${selectedLocation.lat},${selectedLocation.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-xl border border-teal-300 bg-teal-400/10 px-3 py-2 text-xs font-black text-teal-200 hover:bg-teal-400/20"
                    >
                      เปิด Google Maps <ExternalLink size={13} />
                    </a>
                    <button
                      type="button"
                      onClick={() => setSelectedLocation(null)}
                      className="rounded-xl border border-slate-700 bg-slate-800 p-2 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                      title="ปิดแผนที่"
                      aria-label="ปิดแผนที่"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
                <iframe
                  title={`ตำแหน่ง${selectedLocation.kind}ของ ${selectedLocation.displayName}`}
                  src={`https://maps.google.com/maps?q=${selectedLocation.lat},${selectedLocation.lng}&z=17&output=embed`}
                  className="h-72 w-full border-0 sm:h-96"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            )}

            <div className="space-y-2">
              <h3 className="text-sm font-black text-slate-900 mb-2">
                รายชื่อพนักงานที่ลงเวลาวันนี้ ({dashboard.data.rows.length} คน)
              </h3>
              {dashboard.data.rows.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {dashboard.data.rows.map((row: any) => (
                    <div key={row.id} className="attendance-row">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700 font-bold text-xs">
                          {row.displayName.slice(0, 2)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-sm text-slate-900 truncate">
                            {row.displayName}
                          </p>
                          <p className="text-xs font-semibold text-slate-500">
                            เข้า:{" "}
                            <span className="font-numeric text-slate-700">
                              {thaiTimeShort(row.checkedInAt)} น.
                            </span>
                          </p>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {typeof row.checkInLat === "number" &&
                              typeof row.checkInLng === "number" && (
                                <button
                                  onClick={() =>
                                    setSelectedLocation({
                                      displayName: row.displayName,
                                      kind: "เช็คอิน",
                                      lat: row.checkInLat,
                                      lng: row.checkInLng,
                                      accuracy: row.checkInAccuracy,
                                      recordedAt: row.checkedInAt,
                                    })
                                  }
                                  className="inline-flex items-center gap-1 rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 text-[10px] font-black text-white hover:bg-teal-800"
                                >
                                  <MapPin size={11} /> พิกัดเข้า
                                </button>
                              )}
                            {typeof row.checkOutLat === "number" &&
                              typeof row.checkOutLng === "number" &&
                              row.checkedOutAt && (
                                <button
                                  onClick={() =>
                                    setSelectedLocation({
                                      displayName: row.displayName,
                                      kind: "เช็คเอาท์",
                                      lat: row.checkOutLat,
                                      lng: row.checkOutLng,
                                      accuracy: row.checkOutAccuracy,
                                      recordedAt: row.checkedOutAt!,
                                    })
                                  }
                                  className="inline-flex items-center gap-1 rounded-lg border border-slate-700 bg-white px-2 py-1 text-[10px] font-black text-slate-800 hover:bg-slate-100"
                                >
                                  <MapPin size={11} /> พิกัดออก
                                </button>
                              )}
                            {typeof row.checkInLat !== "number" && (
                              <span className="text-[10px] font-bold text-slate-400">
                                ไม่มีข้อมูลพิกัด
                              </span>
                            )}
                            {row.lateReason && (
                              <p className="mt-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] font-bold text-amber-900">
                                มาสาย {row.lateMinutes || 0} นาที · เหตุผล: {row.lateReason} · รอแอดมินพิจารณา
                              </p>
                            )}
                            {row.lateReason && row.lateReviewStatus === "pending" && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                <Button
                                  size="sm"
                                  className="h-8 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-xs"
                                  disabled={decideLateReason.isPending || !idToken}
                                  onClick={() => idToken && decideLateReason.mutate({ idToken, attendanceId: row.id, reviewStatus: "approved" })}
                                >
                                  อนุมัติเหตุผล
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 rounded-xl border-rose-200 text-rose-700 hover:bg-rose-50 font-bold text-xs"
                                  disabled={decideLateReason.isPending || !idToken}
                                  onClick={() => idToken && decideLateReason.mutate({ idToken, attendanceId: row.id, reviewStatus: "rejected" })}
                                >
                                  ไม่อนุมัติ
                                </Button>
                              </div>
                            )}
                            {row.lateReason && row.lateReviewStatus !== "pending" && (
                              <p className="mt-1 text-sm font-bold text-slate-700">ผลพิจารณา: {row.lateReviewStatus === "approved" ? "อนุมัติ" : "ไม่อนุมัติ"}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="text-right shrink-0">
                        {row.checkedOutAt ? (
                          <>
                            <span className="inline-block rounded-md bg-emerald-50 px-2.5 py-0.5 text-xs font-black text-emerald-700 border border-emerald-100">
                              ออก {thaiTimeShort(row.checkedOutAt)} น.
                            </span>
                            <p className="text-[11px] font-semibold text-teal-700 mt-0.5">
                              {workDuration(row.checkedInAt, row.checkedOutAt)}
                            </p>
                          </>
                        ) : (
                          <span className="inline-block rounded-md bg-amber-50 px-2.5 py-0.5 text-xs font-black text-amber-700 border border-amber-100">
                            กำลังทำงาน
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl bg-slate-50 p-8 text-center text-sm font-semibold text-slate-400">
                  ยังไม่มีพนักงานลงเวลาในวันนี้
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="glass-panel rounded-3xl border-white/80 shadow-md">
        <CardHeader className="p-5 sm:p-6 pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                <Search size={16} />
              </div>
              <CardTitle className="text-base font-black text-slate-900">
                ค้นหารายชื่อพนักงานในระบบ
              </CardTitle>
            </div>
            <span className="text-xs font-bold text-slate-400 font-numeric">
              พบ {filtered.length} รายการ
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-5 sm:p-6 pt-0 space-y-4">
          <div className="relative">
            <Search
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
              size={16}
            />
            <input
              aria-label="ค้นหารายชื่อพนักงาน"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="พิมพ์ชื่อ-นามสกุลเพื่อค้นหา…"
              className="glass-input h-11 w-full rounded-2xl pl-10 pr-4 text-sm font-semibold text-slate-900 outline-none"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={15} />
              </button>
            )}
          </div>

          <div className="grid gap-2 sm:grid-cols-2 max-h-72 overflow-y-auto pr-1">
            {filtered.slice(0, 30).map((name, index) => (
              <div key={name} className="attendance-row py-2.5">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-xs font-black text-slate-600 font-numeric">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <p className="truncate font-bold text-xs sm:text-sm text-slate-900">
                    {name}
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className="text-[10px] shrink-0 font-bold border-teal-200 text-teal-700"
                >
                  พนักงาน
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
